import React from 'react';
import { Box, Card, CardContent, Skeleton, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { OutstandingReviewsByBrand } from '../hooks/useOutstandingReviewsByBrand';

interface Props {
  data: OutstandingReviewsByBrand[];
  loading?: boolean;
  height?: number;
  onBrandClick?: (brand: string) => void;
}

export const OutstandingReviewsChart: React.FC<Props> = ({
  data,
  loading = false,
  height = 360,
  onBrandClick,
}) => {
  const chartHeight = Math.max(height, (data?.length || 0) * 28 + 48);

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Outstanding listing reviews by brand
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
          Unreviewed stage-2 product listings waiting in Reviews
        </Typography>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={height} />
        ) : data.length > 0 ? (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={data}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="brand" width={140} tick={{ fontSize: 12 }} />
              <RechartsTooltip formatter={(value: number) => [value, 'Outstanding']} />
              <Bar
                dataKey="outstanding"
                fill="#1976d2"
                radius={[0, 4, 4, 0]}
                cursor={onBrandClick ? 'pointer' : undefined}
                onClick={(entry: any) => {
                  const brandName = entry?.brand || entry?.payload?.brand;
                  if (brandName && onBrandClick) onBrandClick(brandName);
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <Box
            sx={{
              height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="textSecondary">No outstanding reviews</Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default OutstandingReviewsChart;
